import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement } from 'chart.js';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import MODELS from '../../../../constants/models';

Chart.register(ArcElement);

const PieChart = ({ typeWiseAllAccuracies }) => {
  const navigate = useNavigate();
  const [chartOptions, setChartOptions] = useState({
    cutoutPercentage: 65,
    radius: '100%',
  });
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newCutoutPercentage = 65;
      let newRadius;

      // Calculate the new cutout percentage based on window width
      if (width > 1700) {
        newCutoutPercentage = 40;
        newRadius = '100%';
      } else if (width <= 1700 && width > 1440) {
        newCutoutPercentage = 40;
        newRadius = '90%';
      } else if (width <= 1440 && width > 1200) {
        newCutoutPercentage = 40;
        newRadius = '80%';
      } else if (width <= 1200 && width > 900) {
        newCutoutPercentage = 40;
        newRadius = '60%';
      } else if (width <= 900 && width < 600) {
        newCutoutPercentage = 20;
        newRadius = '20%';
      }

      setChartOptions({
        ...chartOptions,
        cutoutPercentage: newCutoutPercentage,
        radius: newRadius,
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartOptions]);

  const data = {
    labels: [
      `Bank Statement ${typeWiseAllAccuracies.bank_statement > 0 ? typeWiseAllAccuracies.bank_statement : '0'}%`,
      `Credit Card Statement ${typeWiseAllAccuracies.credit_card_statement > 0 ? typeWiseAllAccuracies.credit_card_statement : '0'}%`,
      `Sales ${typeWiseAllAccuracies.sales > 0 ? typeWiseAllAccuracies.sales : '0'}%`,
      `Direct Expenses ${typeWiseAllAccuracies.invoice > 0 ? typeWiseAllAccuracies.invoice : '0'}%`,
      `Indirect Expenses ${typeWiseAllAccuracies.receipt > 0 ? typeWiseAllAccuracies.receipt : '0'}%`,
    ],
    datasets: [
      {
        label: 'My First Dataset',
        data: [
          typeWiseAllAccuracies.bank_statement,
          typeWiseAllAccuracies.credit_card_statement,
          typeWiseAllAccuracies.sales,
          typeWiseAllAccuracies.invoice,
          typeWiseAllAccuracies.receipt,
        ],
        borderWidth: 0,
        hoverOffset: 0,
        hoverBorderWidth: 10,
        radius: chartOptions.radius,
        backgroundColor: [
          'rgba(255, 95, 99, 1)',
          'rgba(159, 119, 235, 1)',
          'rgba(255, 159, 49, 1)',
          'rgba(0, 191, 111, 1)',
          'rgba(0, 123, 255, 1)',
        ],
        hoverBackgroundColor: [
          'rgba(255, 95, 99, 1)',
          'rgba(159, 119, 235, 1)',
          'rgba(255, 159, 49, 1)',
          'rgba(0, 191, 111, 1)',
          'rgba(0, 123, 255, 1)',
        ],
        borderColor: [
          'rgba(255, 95, 99, 1)',
          'rgba(159, 119, 235, 1)',
          'rgba(255, 159, 49, 1)',
          'rgba(0, 191, 111, 1)',
          'rgba(0, 123, 255, 1)',
        ],
        hoverBorderColor: [
          'rgba(255, 95, 99, 1)',
          'rgba(159, 119, 235, 1)',
          'rgba(255, 159, 49, 1)',
          'rgba(0, 191, 111, 1)',
          'rgba(0, 123, 255, 1)',
        ],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onResize: (chart) => {
      const chartOption = chart;
      const screenWidth = window.innerWidth;

      let fontSize;

      if (screenWidth >= 1700) {
        fontSize = 16;
      } else if (screenWidth >= 1440 && screenWidth < 1700) {
        fontSize = 14;
      } else if (screenWidth >= 1200 && screenWidth < 1440) {
        fontSize = 12;
      } else if (screenWidth >= 900 && screenWidth < 1200) {
        fontSize = 10;
      } else {
        fontSize = 8;
      }

      chartOption.options.plugins.legend.labels = {
        boxWidth: 20,
        font: { size: fontSize },
      };

      chart.update();
    },
    cutoutPercentage: chartOptions.cutoutPercentage,
    layout: {
      padding: {
        top: 10,
      },
    },
    plugins: {
      legend: {
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          boxHeight: 10,
          boxWidth: 10,
          spacing: 80,
          fontSize: 30,
        },
        position: 'right',
        align: 'start',
      },
      tooltip: {
        displayColors: false,
        yAlign: 'bottom',
        caretPadding: 5,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        bodyFontColor: 'red',
        titleColor: 'rgba(0, 0, 0, 1)',
        callbacks: {
          title: () => null,
          // beforeTitle: (context) => `${context[0].formattedValue}%`,
          label: (tooltipItem) => tooltipItem.label,
          labelTextColor: () => 'rgba(0, 0, 0, 0.6)',
        },
      },
    },
    onClick: (evt, element) => {
      if (element.length > 0) {
        const dataIndex = element[0].index;
        const label = data.labels[dataIndex];

        switch (label) {
          case 'Bank Statement':
            navigate(`/accuracy-details/${MODELS.BANK_STATEMENT}`);
            break;
          case 'Credit Card Statement':
            navigate(`/accuracy-details/${MODELS.CREDIT_CARD_STATEMENT}`);
            break;
          case 'Sales':
            navigate(`/accuracy-details/${MODELS.SALES}`);
            break;
          case 'Direct Expenses':
            navigate(`/accuracy-details/${MODELS.INVOICE}`);
            break;
          case 'Indirect Expenses':
            navigate(`/accuracy-details/${MODELS.RECEIPT}`);
            break;
          default:
            break;
        }
      }
    },
  };

  return (
    <Box sx={{ width: '100%', height: '300px' }}>
      <Doughnut data={data} options={options} />
    </Box>
  );
};

PieChart.defaultProps = {
  typeWiseAllAccuracies: {},
};

PieChart.propTypes = {
  typeWiseAllAccuracies: PropTypes.object,
};

export default PieChart;
